import React, { useEffect, useRef } from 'react';
import { css } from '@emotion/react';
import MapLibreGL, { type StyleSpecification } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import naturalEarth from '@birdi/map-kit/basemaps/natural-earth.json';
import openmap from '@birdi/map-kit/basemaps/openmap.json';
import MapPinSVG from '@birdi/icons/map-pin.svg';
import type { MapSiteBbox } from '@birdi/js-sdk/src/public/map-sites';

// Reuse the app's "Street" base map — the same natural-earth relief +
// openmaptiles street combo the main Birdi map renders (see
// map/next/maplibre/controller.ts `baseMaps.map`) — so this preview matches
// the street view used elsewhere (map view, cover thumbnails). glyphs/sprite
// mirror base-styles.json so labels render.
const STREET_STYLE = {
  version: 8,
  glyphs: 'https://orangemug.github.io/font-glyphs/glyphs/{fontstack}/{range}.pbf',
  sprite:
    'https://birdi-public.s3-ap-southeast-2.amazonaws.com/static/sprites-23/sprites',
  sources: { ...naturalEarth.sources, ...openmap.sources },
  layers: [...naturalEarth.layers, ...openmap.layers],
} as unknown as StyleSpecification;

interface StaticMapPreviewProps {
  center: [number, number];
  bbox?: MapSiteBbox | null;
  /** Text shown in the 📍 chip overlay */
  label?: string | null;
  height?: string;
}

export const StaticMapPreview: React.FC<StaticMapPreviewProps> = ({
  center,
  bbox,
  label,
  height = '14rem',
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return undefined;
    const map = new MapLibreGL.Map({
      container: containerRef.current,
      style: STREET_STYLE,
      center,
      zoom: 13,
      interactive: false,
      attributionControl: false,
    });
    if (bbox) {
      // bbox is [[ne lng, ne lat], [sw lng, sw lat]]
      const [[neLng, neLat], [swLng, swLat]] = bbox;
      map.fitBounds(
        [
          [swLng, swLat],
          [neLng, neLat],
        ],
        { padding: 24, duration: 0 },
      );
    }
    return () => {
      map.remove();
    };
  }, [center[0], center[1], JSON.stringify(bbox)]);

  return (
    <div
      css={css`
        position: relative;
        width: 100%;
        height: ${height};
        border-radius: 8px;
        overflow: hidden;
      `}
    >
      <div
        ref={containerRef}
        css={css`
          width: 100%;
          height: 100%;
        `}
      />
      {label && (
        <div
          css={css`
            position: absolute;
            top: 0.75rem;
            left: 0.75rem;
            display: flex;
            align-items: center;
            gap: 0.35rem;
            background: rgba(255, 255, 255, 0.92);
            border-radius: 1em;
            padding: 0.3em 0.75em;
            font-size: 0.85rem;
            max-width: calc(100% - 2rem);
            box-shadow: 0 1px 4px rgba(9, 30, 66, 0.2);
            svg {
              width: 1em;
              height: 1em;
              flex-shrink: 0;
            }
            span {
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
          `}
        >
          <MapPinSVG />
          <span>{label}</span>
        </div>
      )}
    </div>
  );
};
